"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import Link from "next/link";
import { FaBookmark, FaRegBookmark, FaEye } from "react-icons/fa";

import { saveRedirectUrl } from "@/redirectAfterLogin";
import { blogViewTracker, formatViews, toViewCount } from "@/lib/blogViews";
import { bookmarkAuthHeaders, clearLocalLogin, looksSignedIn, unauthorizedKind } from "@/lib/bookmarkAuth";
import { BLOG_API_ORIGIN } from "@/lib/blogApi";

const API = `${BLOG_API_ORIGIN}/api/blogs`;

// View count (metadata row) and bookmark toggle (share action row, right after
// the share buttons). Both are tiny client islands; the article stays server-rendered.
//
// Bookmark credentials come from lib/bookmarkAuth: the signed token the login
// endpoint returned ("Authorization: Bearer ...") or the Google session cookie.
// An email is never sent as an identity.
//
// A 401 is never reported as just "not signed in": if this browser still holds a
// local login but the server does not accept it (no token was issued, the login
// predates bookmarks, or the token expired) the user is told exactly that and can
// sign in again in one click - see authIssue.
export default function BlogEngagement({ blogId, initialViews, part }) {
  const id = Number(blogId);
  const valid = Number.isInteger(id) && id > 0;

  const [views, setViews] = useState(toViewCount(initialViews));
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  // "" | "login" (signed out) | "no-token" | "token-rejected" (signed in locally, not accepted by the server)
  const [authIssue, setAuthIssue] = useState("");
  const [authTick, setAuthTick] = useState(0);
  const busyRef = useRef(false);

  // --- view tracking: lib/blogViews sends at most one request per eligible
  // visit (shared across Strict Mode double effects and remounts) and returns
  // the server's authoritative count ---
  useEffect(() => {
    if (part !== "views" || !valid) return undefined;
    let active = true;
    blogViewTracker.track(id).then((count) => {
      if (active && count !== null) setViews(count);
    });
    return () => {
      active = false;
    };
  }, [id, valid, part]);

  // The login state can change without this component remounting (login in another
  // tab, token stored by a just-finished flow, logout): re-check when storage changes.
  useEffect(() => {
    if (part !== "bookmark") return undefined;
    const onChange = () => setAuthTick((n) => n + 1);
    window.addEventListener("storage", onChange);
    return () => window.removeEventListener("storage", onChange);
  }, [part]);

  // --- saved state, asked whenever the browser looks signed in ---
  useEffect(() => {
    if (part !== "bookmark" || !valid) return undefined;
    if (!looksSignedIn()) return undefined;
    const controller = new AbortController();
    fetch(`${API}/${id}/bookmark`, { credentials: "include", headers: bookmarkAuthHeaders(), signal: controller.signal })
      .then((res) => {
        if (res.status === 401) {
          // Signed in locally, but the server does not accept these credentials.
          setSaved(false);
          setAuthIssue(unauthorizedKind());
          return null;
        }
        setAuthIssue("");
        return res.ok ? res.json() : null;
      })
      .then((data) => {
        if (data) setSaved(Boolean(data.bookmarked));
      })
      .catch(() => {});
    return () => controller.abort();
  }, [id, valid, part, authTick]);

  const signInAgain = useCallback(() => {
    saveRedirectUrl();
    clearLocalLogin();
    window.dispatchEvent(new Event("storage"));
    window.location.href = "/login";
  }, []);

  if (!valid) return null;

  if (part === "views") {
    // Icon + text in one nowrap flex line (see .blog-view-count in Blogs.css).
    // "— views" means the server count is unavailable, never a real value.
    return (
      <span className="blog-view-count" aria-live="off" title={views === null ? "View count unavailable" : undefined}>
        <FaEye className="blog-view-count__icon" aria-hidden="true" />
        <span className="blog-view-count__text">{views !== null ? formatViews(views) : "— views"}</span>
      </span>
    );
  }

  const toggle = async () => {
    if (busyRef.current) return; // ignore repeated clicks while a request is in flight
    busyRef.current = true;
    setBusy(true);
    setMessage("");
    try {
      const res = await fetch(`${API}/${id}/bookmark`, {
        method: saved ? "DELETE" : "PUT",
        credentials: "include",
        headers: bookmarkAuthHeaders(),
      });
      if (res.status === 401) {
        // Genuinely signed out ("login"), or signed in locally with credentials the
        // server rejects ("no-token" / "token-rejected") - told apart, not conflated.
        saveRedirectUrl();
        setSaved(false);
        setAuthIssue(unauthorizedKind());
        return;
      }
      if (!res.ok) throw new Error(String(res.status));
      const data = await res.json();
      setAuthIssue("");
      setSaved(Boolean(data.bookmarked));
      setMessage(data.bookmarked ? "Saved to your bookmarks" : "Removed from your bookmarks");
    } catch {
      setMessage("Could not update your bookmark. Please try again.");
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const label = saved ? "Saved" : "Save";
  return (
    <span className="blog-bookmark">
      <button
        type="button"
        className={`blog-bookmark__btn${saved ? " is-saved" : ""}`}
        onClick={toggle}
        disabled={busy}
        aria-pressed={saved}
        aria-label={saved ? "Remove bookmark" : "Bookmark this blog"}
        title={saved ? "Remove bookmark" : "Bookmark this blog"}
      >
        {saved ? <FaBookmark aria-hidden="true" /> : <FaRegBookmark aria-hidden="true" />}
        <span className="blog-bookmark__label">{label}</span>
      </button>
      <span className="blog-bookmark__status" role="status" aria-live="polite" data-auth-issue={authIssue || undefined}>
        {authIssue === "login" ? (
          <>
            <Link href="/login" onClick={saveRedirectUrl}>Sign in</Link> to save this blog
          </>
        ) : authIssue ? (
          <>
            {authIssue === "token-rejected" ? "Your session has expired." : "Your login needs a refresh to save blogs."}{" "}
            <button type="button" className="blog-bookmark__relogin" onClick={signInAgain}>
              Sign in again
            </button>
          </>
        ) : (
          message
        )}
      </span>
    </span>
  );
}
